/**
 * Session-draft store (IndexedDB).
 *
 * Persists the FULL session draft (text, PDFs, images, manual document
 * drafts, extracted markdown, flags) per patient until the doctor logs
 * out. Audio blobs are handled separately by audioSessionStore.
 *
 * On logout, `clearAllSessionDrafts()` wipes everything.
 * On login, stale entries (different user or different day) are purged.
 */

import type {
  SessionDraft,
  ImageAttachment,
  PdfAttachment,
  ActionDrafts,
  IncludeInAIFlags,
  AIContextIncludeFlags,
  CommittedDocs,
  DocumentInstance,
  SelectedHistoryRecord,
} from '@/pages/patients/tabs/sessionDraft';
import {
  EMPTY_DRAFTS,
  EMPTY_INCLUDE_IN_AI,
  migrateExtraInstances,
} from '@/pages/patients/tabs/sessionDraft';
import type { EncounterExamsState } from '@/types/encounter';
import { todayLocalISO } from './audioSessionStore';

const DB_NAME = 'dratento-session-drafts';
const DB_VERSION = 1;
const STORE = 'drafts';

// ── Serialisable shapes (File → Blob + metadata) ──────────────────

interface StoredFile {
  name: string;
  type: string;
  blob: Blob;
  /** "Usar como contexto da IA" (ausente = true) — ver ImageAttachment. */
  includeInAI?: boolean;
}

interface StoredSessionDraft {
  id: string;                   // `${userId}::${patientId}`
  userId: string;
  patientId: string;
  date: string;                 // YYYY-MM-DD
  updatedAt: number;
  // Persisted fields
  sessionText: string;
  pdfs: StoredFile[];
  images: StoredFile[];
  extractedMarkdown: string;
  hasExtracted: boolean;
  drafts: ActionDrafts;
  includeInAI: IncludeInAIFlags;
  /** Instâncias extras (atestados/laudos/receitas múltiplos). Pode estar
   *  ausente em registros antigos — é tratado como `[]` na leitura. */
  extraInstances?: DocumentInstance[];
  /** Instruções extras do médico pra orientar a IA na próxima extração. */
  aiInstructions?: string;
  /** Exames modulares do atendimento (Lensometria, Refração, PIO…). Ausente
   *  em registros antigos — tratado como `undefined` na leitura. */
  encounterExams?: EncounterExamsState;
  /** Flags do carry-over dos exames: já tentou pré-popular da última consulta
   *  e a data dessa consulta (pro banner "valores da última consulta"). */
  encounterCarryOverDone?: boolean;
  encounterCarryOverFromDate?: number;
  /** Registros anteriores marcados pra entrar no contexto da IA. Ausente em
   *  registros antigos — tratado como `[]` na leitura. */
  selectedHistory?: SelectedHistoryRecord[];
  /** Seções habilitadas na solicitação principal (modal Solicitações). */
  solicitacaoMain?: { includeExams: boolean; includeSurg: boolean };
  /** Flags "usar na IA" por áudio do dia (mesmo índice dos blobs guardados no
   *  audioSessionStore). Índice ausente = true. */
  audioIncludeInAI?: boolean[];
  /** Flags das fontes de texto no contexto da IA (bloco "Contexto da IA").
   *  Ausente em registros antigos = tudo entra. */
  aiContextInclude?: AIContextIncludeFlags;
  /** Acompanhante do dia — precisa sobreviver a reload/troca de aba durante
   *  o mesmo atendimento, igual ao resto da sessão. */
  accompanistId?: string;
  accompanistName?: string;
  accompanistCpf?: string;
  /** Snapshots dos documentos já salvos na ficha — sem persistir, um
   *  reload traria de volta ao editor o que já virou registro. */
  committedDocs?: CommittedDocs;
  /** Documentos já emitidos nesta sessão (não some no prune) — persistido
   *  pra o reload não voltar a pré-preencher um doc já impresso. */
  issuedDocs?: SessionDraft['issuedDocs'];
  /** Nome antigo do campo acima (quando cobria só documentos de texto) —
   *  lido como fallback pra rascunhos gravados por builds anteriores. */
  committedTextDocs?: CommittedDocs;
}

// ── DB helpers ─────────────────────────────────────────────────────

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') { resolve(null); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'id' });
        os.createIndex('userId', 'userId', { unique: false });
        os.createIndex('date', 'date', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => { resolve(null); };
  });
}

function tx(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(STORE, mode).objectStore(STORE);
}

function makeId(userId: string, patientId: string): string {
  return `${userId}::${patientId}`;
}

// ── Public API ─────────────────────────────────────────────────────

/**
 * Persist the non-audio parts of a SessionDraft to IndexedDB.
 * Audio blobs are NOT stored here (handled by audioSessionStore).
 */
export async function putSessionDraft(
  userId: string,
  patientId: string,
  session: SessionDraft,
): Promise<void> {
  const db = await openDb();
  if (!db) return;

  const storedPdfs: StoredFile[] = session.pdfs.map((p) => ({
    name: p.file.name,
    type: p.file.type,
    blob: p.file,
    includeInAI: p.includeInAI,
  }));

  const storedImages: StoredFile[] = session.images.map((img) => ({
    name: img.file.name,
    type: img.file.type,
    blob: img.file,
    includeInAI: img.includeInAI,
  }));

  const rec: StoredSessionDraft = {
    id: makeId(userId, patientId),
    userId,
    patientId,
    date: todayLocalISO(),
    updatedAt: Date.now(),
    sessionText: session.sessionText,
    pdfs: storedPdfs,
    images: storedImages,
    extractedMarkdown: session.extractedMarkdown,
    hasExtracted: session.hasExtracted,
    drafts: session.drafts,
    includeInAI: session.includeInAI,
    extraInstances: session.extraInstances,
    solicitacaoMain: session.solicitacaoMain,
    aiInstructions: session.aiInstructions,
    encounterExams: session.encounterExams,
    encounterCarryOverDone: session.encounterCarryOverDone,
    encounterCarryOverFromDate: session.encounterCarryOverFromDate,
    selectedHistory: session.selectedHistory,
    audioIncludeInAI: session.audioIncludeInAI,
    aiContextInclude: session.aiContextInclude,
    accompanistId: session.accompanistId,
    accompanistName: session.accompanistName,
    accompanistCpf: session.accompanistCpf,
    committedDocs: session.committedDocs,
    issuedDocs: session.issuedDocs,
  };

  return new Promise((resolve) => {
    const r = tx(db, 'readwrite').put(rec);
    r.onsuccess = () => { db.close(); resolve(); };
    r.onerror = () => { db.close(); resolve(); };
  });
}

/** Restore a previously persisted session draft (today only). */
export async function getSessionDraft(
  userId: string,
  patientId: string,
): Promise<Partial<SessionDraft> | null> {
  const db = await openDb();
  if (!db) return null;

  return new Promise((resolve) => {
    const r = tx(db, 'readonly').get(makeId(userId, patientId));
    r.onsuccess = () => {
      const rec = r.result as StoredSessionDraft | undefined;
      db.close();
      if (!rec || rec.date !== todayLocalISO()) { resolve(null); return; }

      let idCounter = Date.now();

      const pdfs: PdfAttachment[] = (rec.pdfs || []).map((sf) => {
        const file = new File([sf.blob], sf.name, { type: sf.type });
        return { id: String(idCounter++), file, includeInAI: sf.includeInAI };
      });

      const images: ImageAttachment[] = (rec.images || []).map((sf) => {
        const file = new File([sf.blob], sf.name, { type: sf.type });
        return {
          id: String(idCounter++),
          file,
          previewUrl: URL.createObjectURL(file),
          includeInAI: sf.includeInAI,
        };
      });

      resolve({
        sessionText: rec.sessionText || '',
        pdfs,
        images,
        extractedMarkdown: rec.extractedMarkdown || '',
        hasExtracted: rec.hasExtracted ?? false,
        // Espalha sobre EMPTY_DRAFTS pra que campo NOVO (ex.: cirurgiasProcs)
        // chegue preenchido em rascunho gravado antes de ele existir.
        drafts: { ...EMPTY_DRAFTS, ...(rec.drafts ?? {}) },
        includeInAI: rec.includeInAI || EMPTY_INCLUDE_IN_AI,
        // Migra instâncias 'exames'/'cirurgias' antigas pro kind combinado
        // 'solicitacao' (idempotente — rascunhos novos passam intactos).
        extraInstances: migrateExtraInstances(rec.extraInstances ?? []),
        solicitacaoMain: rec.solicitacaoMain,
        aiInstructions: rec.aiInstructions ?? '',
        encounterExams: rec.encounterExams,
        encounterCarryOverDone: rec.encounterCarryOverDone,
        encounterCarryOverFromDate: rec.encounterCarryOverFromDate,
        selectedHistory: rec.selectedHistory ?? [],
        audioIncludeInAI: rec.audioIncludeInAI ?? [],
        aiContextInclude: rec.aiContextInclude,
        accompanistId: rec.accompanistId,
        accompanistName: rec.accompanistName,
        accompanistCpf: rec.accompanistCpf,
        committedDocs: rec.committedDocs ?? rec.committedTextDocs,
        issuedDocs: rec.issuedDocs,
      });
    };
    r.onerror = () => { db.close(); resolve(null); };
  });
}

/** Delete the session draft for a specific patient. */
export async function deleteSessionDraft(
  userId: string,
  patientId: string,
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    const r = tx(db, 'readwrite').delete(makeId(userId, patientId));
    r.onsuccess = () => { db.close(); resolve(); };
    r.onerror = () => { db.close(); resolve(); };
  });
}

// ── Rascunho da "Nova evolução" (aba Prontuário) ───────────────────
//
// O texto digitado na "Nova evolução" vive no PatientRecordDraft (estado da
// página). Persistimos AQUI o texto + o modelo escolhido pra ele sobreviver a
// sair da ficha / recarregar — some só ao salvar ou descartar. Mesma vida útil
// dos rascunhos de sessão: por usuário+paciente, válido no DIA, limpo no
// logout (`clearAllSessionDrafts` faz `clear()` da store inteira) e no login
// (`purgeStaleSessionDrafts` varre por userId/date). Guardamos numa chave
// distinta (`::record`) no MESMO object store — não colide com a sessão.

interface StoredRecordDraft {
  id: string;          // `${userId}::${patientId}::record`
  userId: string;
  patientId: string;
  date: string;        // YYYY-MM-DD
  updatedAt: number;
  newContent: string;          // HTML rico da Nova Evolução
  selectedTemplateId: string;  // modelo pré-elaborado selecionado
}

function makeRecordId(userId: string, patientId: string): string {
  return `${userId}::${patientId}::record`;
}

/** Persiste o rascunho de texto da Nova Evolução. */
export async function putRecordDraft(
  userId: string,
  patientId: string,
  data: { newContent: string; selectedTemplateId: string },
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  const rec: StoredRecordDraft = {
    id: makeRecordId(userId, patientId),
    userId,
    patientId,
    date: todayLocalISO(),
    updatedAt: Date.now(),
    newContent: data.newContent,
    selectedTemplateId: data.selectedTemplateId,
  };
  return new Promise((resolve) => {
    const r = tx(db, 'readwrite').put(rec);
    r.onsuccess = () => { db.close(); resolve(); };
    r.onerror = () => { db.close(); resolve(); };
  });
}

/** Restaura o rascunho de texto da Nova Evolução (só do dia atual). */
export async function getRecordDraft(
  userId: string,
  patientId: string,
): Promise<{ newContent: string; selectedTemplateId: string } | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    const r = tx(db, 'readonly').get(makeRecordId(userId, patientId));
    r.onsuccess = () => {
      const rec = r.result as StoredRecordDraft | undefined;
      db.close();
      if (!rec || rec.date !== todayLocalISO()) { resolve(null); return; }
      resolve({
        newContent: rec.newContent || '',
        selectedTemplateId: rec.selectedTemplateId || '',
      });
    };
    r.onerror = () => { db.close(); resolve(null); };
  });
}

/** Apaga o rascunho de texto da Nova Evolução de um paciente. */
export async function deleteRecordDraft(
  userId: string,
  patientId: string,
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    const r = tx(db, 'readwrite').delete(makeRecordId(userId, patientId));
    r.onsuccess = () => { db.close(); resolve(); };
    r.onerror = () => { db.close(); resolve(); };
  });
}

/** Wipe ALL session drafts. Called from logout. */
export async function clearAllSessionDrafts(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    const r = tx(db, 'readwrite').clear();
    r.onsuccess = () => { db.close(); resolve(); };
    r.onerror = () => { db.close(); resolve(); };
  });
}

/** Purge stale entries (different user or different day). */
export async function purgeStaleSessionDrafts(currentUserId: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  const today = todayLocalISO();
  return new Promise((resolve) => {
    const store = tx(db, 'readwrite');
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) { db.close(); resolve(); return; }
      const rec = cursor.value as StoredSessionDraft;
      if (rec.date !== today || rec.userId !== currentUserId) {
        cursor.delete();
      }
      cursor.continue();
    };
    req.onerror = () => { db.close(); resolve(); };
  });
}
